/* eslint-disable camelcase */

/**
 * @typedef {'network'|'rate_limited'|'not_found'|'invalid_code'|'expired_code'|'unknown'} AuthErrorCode
 */

/**
 * Turns an auth endpoint's status + body into one of the codes the UI has copy for.
 * Mirrors the mapping the iOS app arrived at against the same endpoints: 401/422 is a bad
 * code (Omneo's code/verify also answers 400), and only the body distinguishes "expired"
 * from "incorrect".
 *
 * @param {number} status
 * @param {any} body
 * @returns {AuthErrorCode}
 */
const mapAuthError = (status, body) => {
  if (status === 0) return 'network'
  if (status === 429) return 'rate_limited'
  if (status === 404) return 'not_found'
  if (status === 400 || status === 401 || status === 422) {
    const fieldError = Object.values(body?.errors || {}).flat()[0]
    const message = `${fieldError || body?.error || body?.message || ''}`.toLowerCase()
    if (message.includes('expir')) return 'expired_code'
    return 'invalid_code'
  }
  return 'unknown'
}

export class OmneoPortal {
  static ENDPOINT__EMAIL = '/api/auth/email'
  static ENDPOINT__CODE_VERIFY = '/api/auth/code/verify'
  static ENDPOINT__SMS = '/api/auth/sms'
  static ENDPOINT__AVAILABILITY = '/api/auth/availability'
  static ENDPOINT__CREATE = '/api/auth/create'

  tenant

  constructor(tenant) {
    this.tenant = tenant
  }

  generateHeaders() {
    return new Headers({
      'Content-Type': 'application/json',
      Authorization: 'Bearer WaTmAMFXWl1VidHDcR6xDbZK'
    })
  }

  generateRequestInit(init) {
    return {
      method: init.method,
      body: JSON.stringify(init.body),
      headers: this.generateHeaders()
    }
  }

  generateOrigin() {
    return GM_STATE.omneo.apiBaseUrl
  }

  generateURL(endpoint) {
    const origin = this.generateOrigin()
    return new URL(`${origin}${endpoint}`)
  }

  async fetch(endpoint, _init, isSilent = false) {
    const url = this.generateURL(endpoint)
    const init = this.generateRequestInit(_init)
    const response = await fetch(url, init)
    try {
      return await response.json()
    } catch (error) {
      if (!isSilent) {
        console.error(error)
      }
      return undefined
    }
  }

  /**
   * Like `fetch`, but keeps the HTTP status. The auth endpoints signal "wrong code",
   * "expired" and "rate limited" through the status line and an `error`/`message` body,
   * all of which `fetch` above throws away.
   *
   * @returns {Promise<{ok: boolean, status: number, body: any}>} status 0 means the
   *   request never completed (offline, DNS, CORS).
   */
  async fetchWithStatus(endpoint, _init) {
    const url = this.generateURL(endpoint)
    const init = this.generateRequestInit(_init)
    try {
      const response = await fetch(url, init)
      let body
      try {
        body = await response.json()
      } catch (error) {
        body = undefined
      }
      return { ok: response.ok, status: response.status, body }
    } catch (error) {
      return { ok: false, status: 0, body: undefined }
    }
  }

  async checkAvailability(body) {
    return await this.fetch(OmneoPortal.ENDPOINT__AVAILABILITY, {
      method: 'POST',
      body
    })
  }

  // Based on create profile endpoint in Omneo API
  // https://docs.omneo.io/reference/createprofile
  async createAccount(data) {
    const response = await this.fetch(OmneoPortal.ENDPOINT__CREATE, {
      method: 'POST',
      body: {
        ...data,
        iFrameRedirectData: {
          iFrame: true,
          urlPath: '/account'
        }
      }
    })

    return response
  }

  /**
   * Sends a 6 digit verification code to `mobile_phone` (E.164, e.g. +61412345678).
   *
   * A 2xx with `data.type === 'new'` means the number has no profile, so no code was sent -
   * the drawer pre-empts that with `checkAvailability`, but the endpoint can still say it.
   *
   * @param {string} mobile_phone
   * @returns {Promise<{ok: boolean, error?: AuthErrorCode}>}
   */
  async getSMSAuthCode(mobile_phone) {
    const { ok, status, body } = await this.fetchWithStatus(OmneoPortal.ENDPOINT__SMS, {
      method: 'POST',
      body: {
        mobile_phone
      }
    })

    if (ok && body?.data?.type === 'new') return { ok: false, error: 'not_found' }
    if (ok) return { ok: true }
    return { ok: false, error: mapAuthError(status, body) }
  }

  /**
   * Exchanges the 6 digit code from `getSMSAuthCode` for a login token.
   *
   * The token comes back base64-wrapped rather than as a bare JWT; it is handed straight
   * back to Omneo by `mobileLogin`, so it is deliberately not decoded here.
   *
   * Omneo matches the code against the number it was sent to and rejects a bare code with 400,
   * so `mobile_phone` must be the same formatted value passed to `getSMSAuthCode`.
   *
   * @param {string} code
   * @param {string} mobile_phone
   * @returns {Promise<{ok: boolean, token?: string, error?: AuthErrorCode}>}
   */
  async loginWithSMS(code, mobile_phone) {
    const { ok, status, body } = await this.fetchWithStatus(OmneoPortal.ENDPOINT__CODE_VERIFY, {
      method: 'POST',
      body: {
        code,
        mobile_phone
      }
    })

    const token = body?.data?.token
    if (ok && token) return { ok: true, token }
    if (ok) return { ok: false, error: 'unknown' }
    return { ok: false, error: mapAuthError(status, body) }
  }

  /**
   * Redirects to the authenticate the customers token.
   * @param {*} token
   * @param {string} [redirectUrl] Path to land on afterwards. Defaults to the profile; the
   *   wishlist drawer passes the product page so a save is not interrupted by a redirect.
   */
  mobileLogin(token, redirectUrl) {
    const urlPath = encodeURIComponent(redirectUrl || '/account')
    const url = `${this.generateOrigin()}/embed?token=${token}&mode=undefined&redirect=undefined&externalRedirect=null&iFrame=true&urlPath=${urlPath}&urlParams=undefined`
    window.location.href = url
  }

  getAccountPath() {
    const host = window.location.host
    return `${host}/account`
  }

  async sendLoginEmail(email, redirectUrl) {
    return await this.fetch(
      OmneoPortal.ENDPOINT__EMAIL,
      {
        method: 'POST',
        body: {
          email,
          iFrame: true,
          urlPath: redirectUrl || '/account'
        }
      },
      true
    )
  }
}
